import { useDispatch } from "react-redux";
import useQueryWithErrorHandling from "utils/useQuery";
import {
  BATCH_PROGRESSION_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import {
  useProcessIdOnSingleProcessPage,
  EMPTY_LOT_ROW,
  useLotInfoForProcess,
} from "./LotInfoBar/lotInfoUtils";
import { updateLotInfo, clearLotInfo } from "providers/GlobalStateProvider";
import { LOT_INFO_GETTER_INTERVAL } from "constants/intervals";
import { useUnmount } from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import type { LotInfoType } from "types/types";
import { isEqual } from "lodash";
import { BATCH_PROGRESSION_QUERY_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

export default function LotInfoGetter() {
  const dispatch = useDispatch();
  const processId = useProcessIdOnSingleProcessPage();
  const { setLoading, lotInfoForProcess } = useLotInfoForProcess();
  const variables = { machemetryIds: [processId] };
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);

  useUnmount(() => {
    if (processId) {
      dispatch(clearLotInfo({ processId }));
    }
  });

  const skip = !ckey || !processId;

  return skip ? null : (
    <Query
      {...{
        client,
        processId,
        variables,
        setLoading,
        lotInfoForProcess,
        dispatch,
      }}
    />
  );
}

function Query({
  client,
  processId,
  variables,
  setLoading,
  lotInfoForProcess,
  dispatch,
}: any) {
  // UPDATE LOT INFO
  // get batch / lot info from midgard
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();

  useQueryWithErrorHandling(
    BATCH_PROGRESSION_QUERY_TITLE,
    BATCH_PROGRESSION_QUERY,
    {
      client,
      variables,
      pollInterval: LOT_INFO_GETTER_INTERVAL * multiplyNetworkRequestIntervals,
      onError: err => {
        console.log(
          "LotInfoGetter query gone wrong: ",
          JSON.stringify(variables)
        );
      },
      onCompleted: data => {
        if (!processId) {
          return;
        }

        const batchProgression: BatchProgressionRowType[] =
          data?.batchProgression;

        // if the sheet is empty, stop loading
        if (batchProgression && batchProgression.length === 0) {
          setLoading(false);
        }

        const lotInfo: LotInfoType | null = batchProgression
          ? getLotInfoFromBatchProgressionData(batchProgression, processId)
          : null;

        if (lotInfo && !isEqual(lotInfoForProcess, lotInfo)) {
          dispatch(updateLotInfo({ processId, lotInfo }));
        }
      },
    }
  );

  return null;
}

export type BatchProgressionRowType = {
  ID: number;
  machemetryId: string;
  lotNumber: string;
  lotStartDateMs: string;
  lotSize: number;
  isCompleted: boolean;
};

export function getLotInfoFromBatchProgressionData(
  batchProgression: BatchProgressionRowType[],
  processId: string
): LotInfoType {
  const batchProgressionArrForProcess = batchProgression.filter(
    row => row && processId === row.machemetryId
  );

  // current batch
  const currentBatchProgression =
    batchProgressionArrForProcess[batchProgressionArrForProcess.length - 2];
  // next batch
  const nextBatchProgression =
    batchProgressionArrForProcess[batchProgressionArrForProcess.length - 1];

  // get the *current* lot data from the second-latest batch
  const {
    lotNumber,
    lotSize,
    lotStartDateMs,
    ID: currentBatchProgressionID,
  } = currentBatchProgression || EMPTY_LOT_ROW;

  // get the *next* lot data from the latest batch
  const { lotNumber: nextLotNumber, lotSize: nextLotSize } =
    nextBatchProgression || EMPTY_LOT_ROW;

  return {
    lotStartDateMs: Number(lotStartDateMs),
    currentLotRowID: String(currentBatchProgressionID || 2), // if we don't get one, assume sheet is empty (ID starts at row 2)
    lotNumber,
    nextLotNumber,
    lotSize: Number(lotSize),
    nextLotSize: Number(nextLotSize),
  };
}
